import React, { useEffect, useState } from "react";
import {
  getDocuments,
  getDocumentById,
  processDocument,
  uploadDocument,
  attachEvidenceToReport,
  getReports,
} from "../api/documentApi";
import { addNotification } from "../services/notificationService";

function normalizeReportsPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.reports)) return payload.reports;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: "auto", label: "Auto-detect" },
  { value: "esg_report", label: "ESG Report" },
  { value: "sustainability_report", label: "Sustainability Report" },
  { value: "annual_report", label: "Annual Report" },
  { value: "policy", label: "Policy" },
  { value: "audit_report", label: "Audit Report" },
  { value: "ghg_inventory", label: "GHG Inventory" },
  { value: "workforce_document", label: "HR / Workforce Document" },
  { value: "governance_document", label: "Governance Document" },
  { value: "supplier_evidence", label: "Supplier Evidence" },
  { value: "invoice_utility_bill", label: "Invoice / Utility Bill" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];

export default function DocumentIntelligencePage() {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [attachingKey, setAttachingKey] = useState("");

  async function loadDocuments() {
    try {
      setError("");
      setLoading(true);

      const [documentData, reportData] = await Promise.all([
        getDocuments(),
        getReports().catch(() => []),
      ]);

      const reportList = normalizeReportsPayload(reportData);

      setDocuments(Array.isArray(documentData) ? documentData : []);
      setReports(reportList);

      if (reportList.length > 0 && !selectedReportId) {
        setSelectedReportId(reportList[0]._id);
      }
    } catch (err) {
      setError(err.message || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUpload(event) {
    event.preventDefault();

    if (!selectedFile) {
      setError("Please select a document first.");
      return;
    }

    try {
      setError("");
      setSuccess("");
      setLoading(true);

      const uploadResult = await uploadDocument(
        selectedFile,
        selectedDocumentType
      );
      const newDocument = uploadResult.document;

      setDocuments((prev) => [newDocument, ...prev]);
      setSelectedFile(null);
      setSelectedDocumentType("auto");

      setProcessingId(newDocument._id);
      const processed = await processDocument(newDocument._id);

      await loadDocuments();

      const fullDocument = await getDocumentById(newDocument._id);
      const analyzedDocument = fullDocument || processed.document;
      setSelectedDocument(analyzedDocument);
      setSuccess("Document uploaded and analyzed successfully.");

      addNotification({
        type: "document",
        priority: "normal",
        title: "Document analysis completed",
        message: `${analyzedDocument?.originalName || selectedFile?.name || "Document"} was uploaded and analysed successfully${analyzedDocument?.detectedType ? ` as ${analyzedDocument.detectedType}` : ""}.`,
        targetPage: "Document Intelligence"
      });
    } catch (err) {
      const uploadError = err.message || "Upload failed";
      setError(uploadError);

      addNotification({
        type: "document",
        priority: "high",
        title: "Document analysis failed",
        message: `${selectedFile?.name || "Document"}: ${uploadError}`,
        targetPage: "Document Intelligence"
      });
    } finally {
      setLoading(false);
      setProcessingId("");
    }
  }

  async function handleSelectDocument(documentId) {
    try {
      setError("");
      setSuccess("");
      const data = await getDocumentById(documentId);
      setSelectedDocument(data);
    } catch (err) {
      setError(err.message || "Failed to open document");
    }
  }

  async function handleProcessDocument(documentId) {
    try {
      setError("");
      setSuccess("");
      setProcessingId(documentId);

      await processDocument(documentId);
      await loadDocuments();

      const data = await getDocumentById(documentId);
      setSelectedDocument(data);
      setSuccess("Document analyzed successfully.");

      addNotification({
        type: "document",
        priority: "normal",
        title: "Document analysis completed",
        message: `${data?.originalName || "Document"} was analysed successfully${data?.detectedType ? ` as ${data.detectedType}` : ""}.`,
        targetPage: "Document Intelligence"
      });
    } catch (err) {
      const processError = err.message || "Failed to process document";
      setError(processError);

      const failedDocument = documents.find((item) => item._id === documentId);
      addNotification({
        type: "document",
        priority: "high",
        title: "Document analysis failed",
        message: `${failedDocument?.originalName || "Document"}: ${processError}`,
        targetPage: "Document Intelligence"
      });
    } finally {
      setProcessingId("");
    }
  }

  async function handleAttachEvidence(evidenceIndex) {
    try {
      if (!selectedDocument?._id) {
        setError("Please select a document first.");
        return;
      }

      if (!selectedReportId) {
        setError("Please select a report before attaching evidence.");
        return;
      }

      setError("");
      setSuccess("");
      setAttachingKey(`${selectedDocument._id}-${evidenceIndex}`);

      await attachEvidenceToReport(
        selectedDocument._id,
        evidenceIndex,
        selectedReportId
      );

      const selectedReport = reports.find(
        (report) => report._id === selectedReportId
      );

      setSuccess(
        selectedReport
          ? `Evidence attached to ${selectedReport.companyName || "selected report"}.`
          : "Evidence attached to report successfully."
      );

      addNotification({
        type: "evidence",
        priority: "normal",
        title: "Evidence attached",
        message: `Evidence from ${selectedDocument.originalName || "the selected document"} was attached to ${selectedReport?.companyName || "the selected report"}.`,
        targetPage: "Evidence Library"
      });
    } catch (err) {
      setError(err.message || "Failed to attach evidence to report.");
    } finally {
      setAttachingKey("");
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>AI Document Intelligence</h1>
          <p style={styles.subtitle}>
            Upload ESG policies, reports, invoices, certificates, and evidence
            files. Ecovanta extracts text, detects ESRS relevance, and
            recommends how to use the document.
          </p>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.grid}>
        <section style={styles.panel}>
          <h2 style={styles.sectionTitle}>Upload Document</h2>

          <form onSubmit={handleUpload} style={styles.uploadBox}>
            <div>
              <label style={styles.label} htmlFor="document-type">
                Document type
              </label>
              <select
                id="document-type"
                value={selectedDocumentType}
                onChange={(event) =>
                  setSelectedDocumentType(event.target.value)
                }
                style={styles.select}
                disabled={loading}
              >
                {DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p style={styles.fieldHint}>
                Choose Auto-detect to let Ecovanta classify the document. A
                selected type is supplied as context and the AI still records
                its detected type after analysis.
              </p>
            </div>

            <input
              type="file"
              accept=".pdf,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={styles.fileInput}
            />

            <button type="submit" disabled={loading} style={styles.primaryBtn}>
              {loading ? "Uploading..." : "Upload & Analyze"}
            </button>
          </form>

          <h2 style={styles.sectionTitle}>Recent Documents</h2>

          {loading && documents.length === 0 ? (
            <p style={styles.muted}>Loading documents...</p>
          ) : documents.length === 0 ? (
            <p style={styles.muted}>No documents uploaded yet.</p>
          ) : (
            <div style={styles.documentList}>
              {documents.map((doc) => (
                <button
                  key={doc._id}
                  type="button"
                  onClick={() => handleSelectDocument(doc._id)}
                  style={{
                    ...styles.documentCard,
                    borderColor:
                      selectedDocument?._id === doc._id ? "#0f766e" : "#e5e7eb",
                  }}
                >
                  <div style={styles.docTopRow}>
                    <strong style={styles.docName}>{doc.originalName}</strong>
                    <span style={statusStyle(doc.status)}>{doc.status}</span>
                  </div>

                  <div style={styles.docMeta}>
                    <span>
                      {doc.detectedType ||
                        doc.selectedType ||
                        doc.documentType ||
                        "Not analyzed yet"}
                    </span>
                    <span>{doc.confidence || 0}% confidence</span>
                  </div>

                  <div style={styles.topicRow}>
                    {(doc.esrsTopics || []).slice(0, 4).map((topic) => (
                      <span key={topic} style={styles.topicPill}>
                        {topic}
                      </span>
                    ))}
                  </div>

                  {doc.status !== "completed" && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleProcessDocument(doc._id);
                      }}
                      disabled={processingId === doc._id}
                      style={styles.secondaryBtn}
                    >
                      {processingId === doc._id ? "Processing..." : "Analyze"}
                    </button>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        <section style={styles.panel}>
          {!selectedDocument ? (
            <div style={styles.emptyState}>
              <h2 style={styles.sectionTitle}>Document Analysis</h2>
              <p style={styles.muted}>
                Select a document to view its ESRS analysis, extracted evidence,
                and recommendations.
              </p>
            </div>
          ) : (
            <DocumentDetails
              document={selectedDocument}
              reports={reports}
              selectedReportId={selectedReportId}
              setSelectedReportId={setSelectedReportId}
              onAttachEvidence={handleAttachEvidence}
              attachingKey={attachingKey}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function DocumentDetails({
  document,
  reports,
  selectedReportId,
  setSelectedReportId,
  onAttachEvidence,
  attachingKey,
}) {
  const hasEvidence = (document.evidence || []).length > 0;

  return (
    <div>
      <h2 style={styles.detailTitle}>{document.originalName}</h2>

      <div style={styles.summaryGrid}>
        <Metric label="Status" value={document.status} />
        <Metric
          label="Selected Type"
          value={
            document.selectedType ||
            document.documentType ||
            "Auto-detect"
          }
        />
        <Metric label="Detected Type" value={document.detectedType || "N/A"} />
        <Metric label="Confidence" value={`${document.confidence || 0}%`} />
      </div>

      <div style={styles.block}>
        <h3 style={styles.blockTitle}>Summary</h3>
        <p style={styles.paragraph}>
          {document.summary || "No summary available yet."}
        </p>
      </div>

      <div style={styles.block}>
        <h3 style={styles.blockTitle}>Relevant ESRS Topics</h3>
        <div style={styles.topicRow}>
          {(document.esrsTopics || []).length > 0 ? (
            document.esrsTopics.map((topic) => (
              <span key={topic} style={styles.topicPill}>
                {topic}
              </span>
            ))
          ) : (
            <p style={styles.muted}>No ESRS topics detected yet.</p>
          )}
        </div>
      </div>

      <div style={styles.block}>
        <h3 style={styles.blockTitle}>Evidence</h3>

        {hasEvidence && (
          <div style={styles.reportSelectorBox}>
            <label style={styles.label} htmlFor="document-evidence-report">
              Select target report
            </label>

            <select
              id="document-evidence-report"
              value={selectedReportId}
              onChange={(e) => setSelectedReportId(e.target.value)}
              style={styles.select}
              disabled={reports.length === 0}
            >
              {reports.length === 0 ? (
                <option value="">No reports available</option>
              ) : (
                reports.map((report) => (
                  <option key={report._id} value={report._id}>
                    {report.companyName || "Untitled report"}
                    {report.reportingYear ? ` (${report.reportingYear})` : ""}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {!hasEvidence ? (
          <p style={styles.muted}>No evidence extracted yet.</p>
        ) : (
          document.evidence.map((item, index) => {
            const isAttaching = attachingKey === `${document._id}-${index}`;

            return (
              <div key={`${item.section}-${index}`} style={styles.evidenceCard}>
                <div style={styles.evidenceHeader}>
                  <strong>
                    {item.esrsStandard && item.disclosure
                      ? `${item.esrsStandard} ${item.disclosure}`
                      : item.section || "Evidence"}
                  </strong>
                  <span>{item.confidence || 0}%</span>
                </div>

                {item.section && (
                  <p style={styles.evidenceSection}>{item.section}</p>
                )}

                <p style={styles.quote}>“{item.quote}”</p>

                {item.reason && <p style={styles.reason}>{item.reason}</p>}

                <button
                  type="button"
                  onClick={() => onAttachEvidence(index)}
                  disabled={!selectedReportId || isAttaching}
                  style={styles.attachBtn}
                >
                  {isAttaching ? "Attaching..." : "Attach to Report"}
                </button>
              </div>
            );
          })
        )}
      </div>

      <div style={styles.block}>
        <h3 style={styles.blockTitle}>Recommendations</h3>
        {(document.recommendations || []).length === 0 ? (
          <p style={styles.muted}>No recommendations available yet.</p>
        ) : (
          <ul style={styles.list}>
            {document.recommendations.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
    </div>
  );
}

function statusStyle(status) {
  const base = {
    ...styles.status,
  };

  if (status === "completed") {
    return { ...base, background: "#dcfce7", color: "#166534" };
  }

  if (status === "processing") {
    return { ...base, background: "#fef3c7", color: "#92400e" };
  }

  if (status === "failed") {
    return { ...base, background: "#fee2e2", color: "#991b1b" };
  }

  return { ...base, background: "#e0f2fe", color: "#075985" };
}

const styles = {
  page: {
    padding: "32px",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "32px",
    fontWeight: 800,
    color: "#0f172a",
    margin: 0,
  },
  subtitle: {
    marginTop: "8px",
    maxWidth: "900px",
    color: "#64748b",
    lineHeight: 1.6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "420px 1fr",
    gap: "24px",
    alignItems: "start",
  },
  panel: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    padding: "22px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#0f172a",
    marginTop: 0,
  },
  uploadBox: {
    display: "grid",
    gap: "12px",
    marginBottom: "28px",
  },
  fileInput: {
    border: "1px dashed #94a3b8",
    borderRadius: "12px",
    padding: "14px",
    background: "#f8fafc",
  },
  primaryBtn: {
    border: 0,
    borderRadius: "12px",
    padding: "12px 16px",
    background: "#0f766e",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },
  secondaryBtn: {
    marginTop: "12px",
    border: "1px solid #0f766e",
    borderRadius: "10px",
    padding: "8px 12px",
    background: "#ffffff",
    color: "#0f766e",
    fontWeight: 800,
    cursor: "pointer",
  },
  attachBtn: {
    marginTop: "12px",
    border: 0,
    borderRadius: "10px",
    padding: "10px 14px",
    background: "#0f766e",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
  },
  error: {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "12px",
    padding: "12px 16px",
    marginBottom: "16px",
  },
  success: {
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
    borderRadius: "12px",
    padding: "12px 16px",
    marginBottom: "16px",
  },
  muted: {
    color: "#64748b",
    lineHeight: 1.6,
  },
  documentList: {
    display: "grid",
    gap: "12px",
  },
  documentCard: {
    textAlign: "left",
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "14px",
    background: "#ffffff",
    cursor: "pointer",
  },
  docTopRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
  },
  docName: {
    color: "#0f172a",
    wordBreak: "break-word",
  },
  docMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "10px",
    color: "#64748b",
    fontSize: "13px",
  },
  status: {
    fontSize: "12px",
    fontWeight: 800,
    borderRadius: "999px",
    padding: "4px 8px",
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  },
  topicRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "12px",
  },
  topicPill: {
    display: "inline-flex",
    borderRadius: "999px",
    padding: "6px 10px",
    background: "#ecfeff",
    color: "#155e75",
    fontSize: "12px",
    fontWeight: 800,
  },
  emptyState: {
    minHeight: "400px",
  },
  detailTitle: {
    fontSize: "24px",
    fontWeight: 900,
    color: "#0f172a",
    marginTop: 0,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "12px",
    marginBottom: "22px",
  },
  metricCard: {
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "14px",
    background: "#f8fafc",
  },
  metricLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: "6px",
  },
  metricValue: {
    color: "#0f172a",
    fontSize: "16px",
  },
  block: {
    borderTop: "1px solid #e5e7eb",
    paddingTop: "18px",
    marginTop: "18px",
  },
  blockTitle: {
    marginTop: 0,
    color: "#0f172a",
    fontSize: "16px",
    fontWeight: 900,
  },
  paragraph: {
    color: "#334155",
    lineHeight: 1.7,
  },
  reportSelectorBox: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "14px",
    marginBottom: "14px",
  },
  label: {
    display: "block",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 800,
    marginBottom: "8px",
  },
  fieldHint: {
    margin: "8px 0 0",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
  },
  evidenceCard: {
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "14px",
    marginBottom: "12px",
    background: "#f8fafc",
  },
  evidenceHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#0f172a",
  },
  evidenceSection: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: 0,
  },
  quote: {
    color: "#334155",
    lineHeight: 1.6,
    marginBottom: 0,
  },
  reason: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
    marginBottom: 0,
  },
  list: {
    color: "#334155",
    lineHeight: 1.7,
    paddingLeft: "20px",
  },
};
